import {
  createDomainRouterBaseApi,
  type TriedgeDomainRouterBaseApi,
} from '@triedge/rtk-abstractions';
import type {
  AdmissionsFeatureBundle,
  AdmissionsFeatureBundleOptions,
  AdmissionsParentProfile,
  AdmissionsRouteManifest,
} from '../types';
import { createMockAdmissionsStorageAdapter, createMockAdmissionsTransport } from './mockAdmissionsTransport';

export { createMockAdmissionsStorageAdapter, createMockAdmissionsTransport } from './mockAdmissionsTransport';
export {
  buildAdmissionsAcceptanceLetterFileName,
  buildAdmissionsAcceptanceLetterPdf,
  buildAdmissionsAcceptanceLetterPdfBytes,
  openAdmissionsAcceptanceLetterPdf,
  renderAcceptanceLetter,
  resolveAcceptanceLetterTemplateVariant,
  resolveChildGenderWords,
} from '../documents/index';
export type {
  AcceptanceLetterContext,
  AcceptanceLetterTemplateVariant,
  AdmissionsAcceptanceLetterInput,
  AdmissionsAcceptanceLetterPdf,
  AdmissionsAcceptanceLetterStudent,
  OpenAdmissionsAcceptanceLetterPdfInput,
} from '../documents/index';

const TAG_TYPES = [
  'AdmissionsApplications',
  'AdmissionsApplication',
  'AdmissionsDocuments',
  'AdmissionsTimeline',
  'AdmissionsTuition',
] as const;

const EMPTY_PARENT_PROFILE: AdmissionsParentProfile = {
  userSub: '',
  email: '',
  name: '',
};

const routeManifestFor = ({
  staffBase,
  parentApplicationsPath,
  parentIntakePath,
}: AdmissionsFeatureBundleOptions['routes']): AdmissionsRouteManifest => ({
  staffBase,
  staffDashboardPath: `${staffBase}/dashboard`,
  staffDetailPath: (applicationId, tab) => `${staffBase}/${tab}/${applicationId}`,
  staffTabs: ['children', 'family', 'documents', 'notes', 'tuition'],
  parentApplicationsPath,
  parentIntakePath,
});

const asMessage = (error: unknown): { message: string } => ({
  message: error instanceof Error ? error.message : String(error ?? 'Unknown admissions error'),
});

const withAdmissionsTags = (baseApi: TriedgeDomainRouterBaseApi) =>
  baseApi.enhanceEndpoints({
    addTagTypes: [...TAG_TYPES],
  });

export const createAdmissionsFeatureBundle = ({
  transport,
  routes,
  baseApi = createDomainRouterBaseApi({ reducerPath: 'admissionsFeatureApi' }),
  reducerPath,
  storage,
  parent,
  routeIntents,
}: AdmissionsFeatureBundleOptions): AdmissionsFeatureBundle => {
  const preparedBaseApi = reducerPath && baseApi.reducerPath !== reducerPath
    ? createDomainRouterBaseApi({ reducerPath })
    : baseApi;
  const admissionsApi = withAdmissionsTags(preparedBaseApi).injectEndpoints({
    endpoints: (builder: any) => ({
      listAdmissionsApplications: builder.query({
        queryFn: async (args: { limit?: number; nextToken?: string | null; projection?: 'full' | 'dashboard' } | void) => {
          try {
            const data = await transport.query('listApplications', {
              limit: args?.limit,
              nextToken: args?.nextToken,
              projection: args?.projection,
            });
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: (result: any) => [
          { type: 'AdmissionsApplications', id: 'LIST' },
          ...((result?.items ?? []).map((item: any) => ({
            type: 'AdmissionsApplication' as const,
            id: String(item?.id ?? ''),
          }))),
        ],
      }),
      getAdmissionsApplication: builder.query({
        queryFn: async (applicationId: string) => {
          try {
            const data = await transport.query('getApplication', { id: applicationId });
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: (_result: unknown, _error: unknown, applicationId: string) => [{ type: 'AdmissionsApplication', id: applicationId }],
      }),
      getApplicantAdmissionsApplications: builder.query({
        queryFn: async (args: { applicantId: string; limit?: number; nextToken?: string | null }) => {
          try {
            const data = await transport.query('getApplicantApplications', {
              applicantId: args.applicantId,
              limit: args.limit,
              nextToken: args.nextToken ?? undefined,
            });
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: (result: any) => [
          { type: 'AdmissionsApplications', id: 'LIST' },
          ...((result?.items ?? []).map((item: any) => ({
            type: 'AdmissionsApplication' as const,
            id: String(item?.id ?? ''),
          }))),
        ],
      }),
      getAdmissionsDocuments: builder.query({
        queryFn: async (args: { applicationId: string; limit?: number }) => {
          try {
            const data = await transport.query('getApplicationDocuments', {
              applicationId: args.applicationId,
              limit: args.limit,
            });
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: (_result: unknown, _error: unknown, args: { applicationId: string }) => [{ type: 'AdmissionsDocuments', id: args.applicationId }],
      }),
      getAdmissionsDocumentDownloadUrl: builder.query({
        queryFn: async (args: { applicationId: string; documentId: string; disposition?: 'attachment' | 'inline' }) => {
          try {
            const data = await transport.query('getApplicationDocumentDownloadUrl', args);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: (_result: unknown, _error: unknown, args: { applicationId: string }) => [{ type: 'AdmissionsDocuments', id: args.applicationId }],
      }),
      getAdmissionsTimeline: builder.query({
        queryFn: async (args: { applicationId: string; limit?: number }) => {
          try {
            const data = await transport.query('listTimelineEvents', {
              filter: JSON.stringify({ applicationId: args.applicationId }),
              limit: args.limit,
            });
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: (_result: unknown, _error: unknown, args: { applicationId: string }) => [{ type: 'AdmissionsTimeline', id: args.applicationId }],
      }),
      resolveFamilyForAdmissionsApplication: builder.query({
        queryFn: async (args: { applicationId: string }) => {
          try {
            const data = await transport.query('resolveFamilyForAdmissionsApplication', args);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: (_result: unknown, _error: unknown, args: { applicationId: string }) => [{ type: 'AdmissionsTuition', id: args.applicationId }],
      }),
      getAdmissionsTuitionConfiguration: builder.query({
        queryFn: async (args: { applicationId: string }) => {
          try {
            const data = await transport.query('getAdmissionsTuitionConfiguration', args);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        providesTags: (_result: unknown, _error: unknown, args: { applicationId: string }) => [{ type: 'AdmissionsTuition', id: args.applicationId }],
      }),
      updateAdmissionsApplicationStatus: builder.mutation({
        queryFn: async (input: { applicationId: string; status: string }) => {
          try {
            const data = await transport.mutate('updateApplicationStatus', input as any);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: (_result: unknown, _error: unknown, input: { applicationId: string }) => [
          { type: 'AdmissionsApplications' },
          { type: 'AdmissionsApplications', id: 'LIST' },
          { type: 'AdmissionsApplication', id: input.applicationId },
        ],
      }),
      scheduleAdmissionsInterview: builder.mutation({
        queryFn: async (input: { applicationId: string; interviewDate: number; staffId?: string }) => {
          try {
            const data = await transport.mutate('scheduleInterview', input as any);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: (_result: unknown, _error: unknown, input: { applicationId: string }) => [
          { type: 'AdmissionsApplications', id: 'LIST' },
          { type: 'AdmissionsApplication', id: input.applicationId },
          { type: 'AdmissionsTimeline', id: input.applicationId },
        ],
      }),
      scheduleAdmissionsVisit: builder.mutation({
        queryFn: async (input: { applicationId: string; visitDate: number; staffId?: string }) => {
          try {
            const data = await transport.mutate('scheduleVisit', input as any);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: (_result: unknown, _error: unknown, input: { applicationId: string }) => [
          { type: 'AdmissionsApplications', id: 'LIST' },
          { type: 'AdmissionsApplication', id: input.applicationId },
          { type: 'AdmissionsTimeline', id: input.applicationId },
        ],
      }),
      updateAdmissionsChildApplication: builder.mutation({
        queryFn: async (input: { childApplicationId: string; status?: string; assignedTo?: string | null; assigneeName?: string; enrollmentDeadline?: number | null }) => {
          try {
            const data = await transport.mutate('updateChildApplication', input as any);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: [{ type: 'AdmissionsApplications', id: 'LIST' }],
      }),
      addAdmissionsNote: builder.mutation({
        queryFn: async (input: { applicationId: string; note: { noteType: string; content: string; isImportant?: boolean; authorName?: string | null } }) => {
          try {
            const data = await transport.mutate('addNote', input as any);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: (_result: unknown, _error: unknown, input: { applicationId: string }) => [
          { type: 'AdmissionsApplication', id: input.applicationId },
          { type: 'AdmissionsTimeline', id: input.applicationId },
        ],
      }),
      deleteAdmissionsNote: builder.mutation({
        queryFn: async (input: { applicationId: string; noteId: string }) => {
          try {
            const data = await transport.mutate('deleteNote', input as any);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: (_result: unknown, _error: unknown, input: { applicationId: string }) => [
          { type: 'AdmissionsApplication', id: input.applicationId },
          { type: 'AdmissionsTimeline', id: input.applicationId },
        ],
      }),
      addAdmissionsDocument: builder.mutation({
        queryFn: async (input: {
          applicationId: string;
          document: {
            resourceId: string;
            category: string;
            uploadedBy?: string | null;
            fileName?: string;
            contentType?: string;
            sizeBytes?: number;
            childApplicationIds?: string[];
          };
        }) => {
          try {
            const data = await transport.mutate('addDocument', input as any);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: (_result: unknown, _error: unknown, input: { applicationId: string }) => [
          { type: 'AdmissionsApplication', id: input.applicationId },
          { type: 'AdmissionsDocuments', id: input.applicationId },
        ],
      }),
      sendAdmissionsApplicationMessage: builder.mutation({
        queryFn: async (input: { applicationId: string; subject: string; message: string }) => {
          try {
            const data = await transport.mutate('sendApplicationMessage', input as any);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: (_result: unknown, _error: unknown, input: { applicationId: string }) => [
          { type: 'AdmissionsTimeline', id: input.applicationId },
        ],
      }),
      upsertAdmissionsTuitionConfiguration: builder.mutation({
        queryFn: async (input: { input: Record<string, unknown> }) => {
          try {
            const data = await transport.mutate('upsertAdmissionsTuitionConfiguration', input as any);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: (_result: unknown, _error: unknown, input: { input: Record<string, unknown> }) => [
          { type: 'AdmissionsTuition', id: String(input.input.applicationId ?? 'LIST') },
        ],
      }),
      generateAdmissionsTuitionContract: builder.mutation({
        queryFn: async (input: { input: Record<string, unknown> }) => {
          try {
            const data = await transport.mutate('generateTuitionContract', input as any);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: (_result: unknown, _error: unknown, input: { input: Record<string, unknown> }) => [
          { type: 'AdmissionsTuition', id: String(input.input.applicationId ?? 'LIST') },
          { type: 'AdmissionsDocuments', id: String(input.input.applicationId ?? 'LIST') },
        ],
      }),
      sendAdmissionsTuitionContract: builder.mutation({
        queryFn: async (input: { input: Record<string, unknown> }) => {
          try {
            const data = await transport.mutate('sendTuitionContract', input as any);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: (_result: unknown, _error: unknown, input: { input: Record<string, unknown> }) => [
          { type: 'AdmissionsTuition', id: String(input.input.applicationId ?? 'LIST') },
          { type: 'AdmissionsTimeline', id: String(input.input.applicationId ?? 'LIST') },
        ],
      }),
      requestAdmissionsInterviewSchedule: builder.mutation({
        queryFn: async (input: { applicationId: string; preferredSlots: number[]; notes?: string }) => {
          try {
            const data = await transport.mutate('requestInterviewSchedule', input as any);
            return { data };
          } catch (error) {
            return { error: asMessage(error) };
          }
        },
        invalidatesTags: (_result: unknown, _error: unknown, input: { applicationId: string }) => [
          { type: 'AdmissionsApplication', id: input.applicationId },
          { type: 'AdmissionsTimeline', id: input.applicationId },
        ],
      }),
    }),
  });

  const manifest = routeManifestFor(routes);
  const parentProfile = parent?.profile ?? EMPTY_PARENT_PROFILE;

  return {
    id: 'admissions',
    api: admissionsApi,
    hooks: {
      useListAdmissionsApplicationsQuery: admissionsApi.useListAdmissionsApplicationsQuery,
      useGetAdmissionsApplicationQuery: admissionsApi.useGetAdmissionsApplicationQuery,
      useGetApplicantAdmissionsApplicationsQuery: admissionsApi.useGetApplicantAdmissionsApplicationsQuery,
      useGetAdmissionsDocumentsQuery: admissionsApi.useGetAdmissionsDocumentsQuery,
      useGetAdmissionsDocumentDownloadUrlQuery: admissionsApi.useGetAdmissionsDocumentDownloadUrlQuery,
      useLazyGetAdmissionsDocumentDownloadUrlQuery: admissionsApi.useLazyGetAdmissionsDocumentDownloadUrlQuery,
      useGetAdmissionsTimelineQuery: admissionsApi.useGetAdmissionsTimelineQuery,
      useResolveFamilyForAdmissionsApplicationQuery: admissionsApi.useResolveFamilyForAdmissionsApplicationQuery,
      useGetAdmissionsTuitionConfigurationQuery: admissionsApi.useGetAdmissionsTuitionConfigurationQuery,
      useUpdateAdmissionsApplicationStatusMutation: admissionsApi.useUpdateAdmissionsApplicationStatusMutation,
      useScheduleAdmissionsInterviewMutation: admissionsApi.useScheduleAdmissionsInterviewMutation,
      useScheduleAdmissionsVisitMutation: admissionsApi.useScheduleAdmissionsVisitMutation,
      useUpdateAdmissionsChildApplicationMutation: admissionsApi.useUpdateAdmissionsChildApplicationMutation,
      useAddAdmissionsNoteMutation: admissionsApi.useAddAdmissionsNoteMutation,
      useDeleteAdmissionsNoteMutation: admissionsApi.useDeleteAdmissionsNoteMutation,
      useAddAdmissionsDocumentMutation: admissionsApi.useAddAdmissionsDocumentMutation,
      useSendAdmissionsApplicationMessageMutation: admissionsApi.useSendAdmissionsApplicationMessageMutation,
      useUpsertAdmissionsTuitionConfigurationMutation: admissionsApi.useUpsertAdmissionsTuitionConfigurationMutation,
      useGenerateAdmissionsTuitionContractMutation: admissionsApi.useGenerateAdmissionsTuitionContractMutation,
      useSendAdmissionsTuitionContractMutation: admissionsApi.useSendAdmissionsTuitionContractMutation,
      useRequestAdmissionsInterviewScheduleMutation: admissionsApi.useRequestAdmissionsInterviewScheduleMutation,
    },
    routeManifest: manifest,
    routeIntents,
    pageRegistry: {
      staffDashboard: 'AdmissionsStaffDashboardPage',
      staffDetail: 'AdmissionsStaffApplicantDetailPage',
      parentApplications: 'AdmissionsParentApplicationsPage',
      parentIntake: 'AdmissionsParentIntakePage',
    },
    storybookFixtures: {
      staff: {
        title: 'Admissions Staff Surfaces',
        initialEntry: manifest.staffDashboardPath,
      },
      parentApplications: {
        title: 'Admissions Parent Applications',
        initialEntry: manifest.parentApplicationsPath,
      },
      parentIntake: {
        title: 'Admissions Parent Intake',
        initialEntry: manifest.parentIntakePath,
      },
    },
    storage,
    parent: {
      profile: parentProfile,
      intakeRequirements: parent?.intakeRequirements,
      hooks: {
        useSelectedApplicationQuery: admissionsApi.useGetAdmissionsApplicationQuery,
        useApplicantApplicationsQuery: admissionsApi.useGetApplicantAdmissionsApplicationsQuery,
      },
      actions: {
        uploadAdmissionsDocument: async (input) => {
          if (!storage?.uploadFile) {
            throw new Error('Admissions document uploads require a configured storage adapter.');
          }

          const uploadResult = await storage.uploadFile({
            applicationId: input.applicationId,
            category: input.category,
            documentName: input.file.name,
            file: input.file,
            childApplicationIds: input.childApplicationIds,
            ownerId: input.ownerId,
            slotKey: input.slotKey,
            storagePath: input.storagePath,
          });
          const uploadedBy = uploadResult.uploadedBy ?? storage.resolveCurrentActorId?.() ?? null;

          return transport.mutate('addDocument', {
            applicationId: input.applicationId,
            document: {
              resourceId: uploadResult.resourceId,
              category: input.category,
              uploadedBy,
              fileName: uploadResult.fileName ?? input.file.name,
              contentType: uploadResult.contentType ?? input.file.type ?? undefined,
              sizeBytes: uploadResult.sizeBytes ?? (Number.isFinite(input.file.size) ? input.file.size : undefined),
              childApplicationIds: input.childApplicationIds,
              storageProvider: 's3',
              storageKey: input.storagePath || uploadResult.resourceId,
            },
          } as any);
        },
        requestInterviewSchedule: async (input) =>
          transport.mutate('requestInterviewSchedule', input as any),
        ensureParentPersonaReady: parent?.ensureParentPersonaReady,
        onApplicationSubmitted: parent?.onApplicationSubmitted,
      },
    },
  };
};

export const createMockAdmissionsFeatureBundle = (
  staffBase = '/school/admissions',
  parentApplicationsPath = '/parent/portal/applications',
  parentIntakePath = '/parent/portal--new/applications',
) =>
  createAdmissionsFeatureBundle({
    transport: createMockAdmissionsTransport(),
    storage: createMockAdmissionsStorageAdapter(),
    routes: {
      staffBase,
      parentApplicationsPath,
      parentIntakePath,
    },
    parent: {
      profile: {
        userSub: 'parent-123',
        email: 'parent@example.com',
        name: 'Parent Example',
      },
      ensureParentPersonaReady: async () => undefined,
      onApplicationSubmitted: () => undefined,
    },
    reducerPath: 'mockAdmissionsFeatureApi',
  });
